import { Injectable } from '@angular/core';

import { MessageEnvelope } from '@cds/push';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ActionCreator, Creator, Store } from '@ngrx/store';
import { buffer, concatMap, filter, map, merge, tap } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipState } from '../../../core/betslip-state';
import { PickId } from '../../../core/picks/pick-id';
import BetslipPickActions from '../actions';
import { addLegSubscription, removePicksFromGroup } from '../grouped-picks.actions';
import { ofTypeReplaceExistingPick } from '../helpers';
import PickSubscriptionActions from '../pick-subscription-actions';
import { selectBetslipFlattenedPicksList } from '../selectors';
import BetslipPickSubscriptionService from '../services/betslip-pick-subscription.service';

@Injectable()
export default class PushEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private subscriptionService: BetslipPickSubscriptionService,
    ) {}

    /**
     * Handle subscribe to pick action
     */
    handleSubscribeToPickActionEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PickSubscriptionActions.subscribeToPick),
                tap((action) => {
                    this.subscriptionService.subscribeToPicks([action.pick]);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    /**
     * Handle unsubscribe from pick action
     */
    handleUnsubscribeFromPickActionEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PickSubscriptionActions.unsubscribeFromPick),
                tap((action) => {
                    this.subscriptionService.unsubscribeFromPicks([action.pickId]);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    /**
     * Handle subscribe to picks action
     */
    handleSubscribeToPicksActionEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PickSubscriptionActions.subscribeToPicks),
                filter((action) => action.picks.length > 0),
                tap((action) => {
                    this.subscriptionService.subscribeToPicks(action.picks);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    /**
     * Handle unsubscribe from picks action
     */
    handleUnsubscribeFromPicksActionEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PickSubscriptionActions.unsubscribeFromPicks),
                filter((action) => action.pickIds.length > 0),
                tap((action) => {
                    this.subscriptionService.unsubscribeFromPicks(action.pickIds);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    /**
     * Handle unsubscribe from all picks action
     */
    handleUnsubscribeAllActionEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(PickSubscriptionActions.unsubscribeAll),
                tap((action) => {
                    this.subscriptionService.unsubscribeFromAll();
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    /**
     * When pick is added subscribe for pick changes.
     */
    handleAddPickActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.addPick),
            filter((action) => action.pick.subscriptionContext.length > 0),
            map((action) =>
                PickSubscriptionActions.subscribeToPick({
                    pick: {
                        pickId: action.pick.id,
                        context: action.pick.subscriptionContext,
                        fromHybridFixture: action.pick.fromHybridFixture,
                    },
                }),
            ),
        );
    });

    /**
     * When pick is added to a group, subscribe for pick changes.
     */
    handleAddPickToGroupActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(addLegSubscription),
            filter((action) => action.subscriptionContext.length > 0),
            map((action) =>
                PickSubscriptionActions.subscribeToPick({
                    pick: {
                        pickId: action.pickId,
                        context: action.subscriptionContext,
                        fromHybridFixture: action.fromHybridFixture,
                    },
                }),
            ),
        );
    });

    /**
     * When pick is removed remove pick subscription.
     */
    handleRemovePickActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick),
            filter((action) => !action.skipPickSubscriptionCleanup),
            map((action) => {
                return PickSubscriptionActions.unsubscribeFromPick({ pickId: action.pickId });
            }),
        );
    });

    /**
     * When pick is removed, remove all children picks subscriptions.
     */
    handleRemovePickChildrenActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick),
            filter((action) => !!action.childrenPickIds && action.childrenPickIds.length > 0),
            map((action) => {
                return PickSubscriptionActions.unsubscribeFromPicks({ pickIds: action.childrenPickIds as PickId[] });
            }),
        );
    });

    /**
     * When pick is removed remove pick subscription.
     */
    handleRemovePicksFromGroupActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(removePicksFromGroup),
            map((action) => {
                return PickSubscriptionActions.unsubscribeFromPicks({ pickIds: action.pickIds });
            }),
        );
    });

    handleReplacePickEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            concatLatestFrom(() => this.store.select(selectBetslipFlattenedPicksList)),
            filter(([action, picks]) => picks.some((p) => p.id.isEqual(action.newPick.id)) && !action.skipPickSubscriptionCleanup),
            concatMap(([action]) => {
                return [
                    PickSubscriptionActions.unsubscribeFromPick({ pickId: action.pickId }),
                    PickSubscriptionActions.subscribeToPick({
                        pick: {
                            pickId: action.newPick.id,
                            context: action.newPick.subscriptionContext,
                            fromHybridFixture: action.newPick.fromHybridFixture,
                        },
                    }),
                ];
            }),
        );
    });

    /**
     * When pick is removed remove pick subscription.
     */
    handleRemovePicksActionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removeMultiplePicks),
            map((action) => PickSubscriptionActions.unsubscribeFromPicks({ pickIds: action.pickIds })),
        );
    });

    handleEventUpdatedBufferEffect$ = this.createBufferedEventUpdater(PickSubscriptionActions.eventUpdated, BetslipActions.eventUpdate);

    handleFixtureUpdatedBufferEffect$ = this.createBufferedEventUpdater(PickSubscriptionActions.fixtureUpdated, BetslipActions.fixtureUpdate);

    handleBetBuilderGroupUpdatedBufferEffect$ = this.createBufferedEventUpdater(
        PickSubscriptionActions.betBuilderGroupUpdated,
        BetslipActions.betBuilderGroupUpdate,
    );

    /**
     * Converts PickSubscriptionActions eventUpdate/fixtureUpdated events to BetslipActions eventUpdate/fixtureUpdate.
     * and buffer them, while placing.
     *
     * @param actionToHandle
     * @param actionToEmit
     */
    // eslint-disable-next-line
    private createBufferedEventUpdater<TInputAction extends string, TOutputAction extends string>(
        actionToHandle: ActionCreator<
            TInputAction,
            Creator<
                [{ message: MessageEnvelope }],
                {
                    message: MessageEnvelope;
                    type: TInputAction;
                }
            >
        >,
        actionToEmit: ActionCreator<
            TOutputAction,
            Creator<
                [{ message: MessageEnvelope }],
                {
                    message: MessageEnvelope;
                    type: TOutputAction;
                }
            >
        >,
    ) {
        return createEffect(() => {
            const switchFromPlacingModeObservable = this.actions$.pipe(
                ofType(BetslipActions.setState),
                filter((action) => action.state !== BetslipState.Placing),
            );

            const receivedEventsWhileInEditMode = this.actions$.pipe(
                ofType(actionToHandle),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                filter(([action, betslipState]) => betslipState.base.state !== BetslipState.Placing),
                map(([action, state]) => actionToEmit({ message: action.message })),
            );

            const bufferedEventsWhileNotInEditMode = this.actions$.pipe(
                ofType(actionToHandle),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                filter(([action, betslipState]) => betslipState.base.state === BetslipState.Placing),
                map(([action, state]) => action),
                buffer(switchFromPlacingModeObservable),
                filter((actions) => actions.length > 0),
                concatMap((actions) => [...actions.map((e) => actionToEmit({ message: e.message }))]),
            );

            return merge(receivedEventsWhileInEditMode, bufferedEventsWhileNotInEditMode);
        });
    }
}
